<template>
  <div class="trip-attachments-view" data-cy="trip-attachments-view">
    <div class="trip-attachments-view__container">
      <div
        class="trip-attachments-view__header"
        data-cy="trip-attachments-header"
      >
        <h3>Uploaded Attachments</h3>
      </div>
      <div v-if="loading" class="trip-attachments-view__attachments">
        <Skeleton
          class="trip-attachments-view__skeleton"
          v-for="index in 3"
          :key="index"
          shape="rectangle"
        />
      </div>
      <div v-else class="trip-attachments-view__attachments">
        <div
          v-for="attachment in existingAttachments"
          :key="attachment.FileUrl"
          class="trip-attachments-view__attachment"
          tabindex="0"
          @click="downloadAttachmentAsync(attachment)"
          data-cy="trip-attachment"
        >
          <DownloadIcon /> {{ attachment.Title }}
        </div>
        <div
          v-if="existingAttachments.length === 0"
          class="trip-attachments-view__attachment default"
        >
          No attachments found
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { onBeforeMount, ref, watch } from "vue";
import { useNotificationStore } from "@/stores/useNotificationStore";
import type { EirSharePointAttachment } from "@/Domain/EirSharePointAttachment";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { Skeleton } from "@progress/kendo-vue-indicators";
import { useExportStore } from "@/stores/useExportStore";
import {
  workbookOptions,
  type WorkbookOptions,
} from "@progress/kendo-vue-excel-export";
import { copyRowsToSheet } from "@/shared/helpers/exportHelper";
import DownloadIcon from "@/icons/DownloadIcon.vue";

const notificationStore = useNotificationStore();
const exportStore = useExportStore();

const props = defineProps<{
  tripId: number;
}>();

const existingAttachments = ref<EirSharePointAttachment[]>([]);
const loading = ref(true);

const getAttachmentsAsync = async function () {
  loading.value = true;

  await handleResponsePromiseAsync(
    api.get("GetEfrUploadedDocs", {
      tripId: props.tripId,
    }),
    (data) => {
      existingAttachments.value = data;
    },
    "TripAttachmentsView.vue",
    "getAttachmentsAsync"
  );

  loading.value = false;
};

const downloadAttachmentAsync = async function (
  attachment: EirSharePointAttachment
) {
  const downloadPromise = api.getDownload("DownloadEfrDoc", {
    fileUrl: attachment.FileUrl,
    fileName: attachment.Title,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "FormAttachmentsView.vue",
    "downloadAttachmentAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading ${attachment.Title}`,
        duration: 5000,
      });
    }
  );
};

const onExport = function (options: WorkbookOptions | null) {
  if (!options) return;

  const optionsTemp = workbookOptions({
    columns: [
      {
        title: "Attachments",
        field: "Title",
      },
    ],
    data: existingAttachments.value,
  });

  copyRowsToSheet(options, optionsTemp);
};

exportStore.subscribe(onExport);

watch(
  () => props.tripId,
  () => {
    getAttachmentsAsync();
  }
);

onBeforeMount(async () => {
  await getAttachmentsAsync();
});
</script>

<style lang="scss">
.trip-attachments-view {
  padding: 16px;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &__header {
    margin-bottom: 12px;
  }

  &__attachments {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__attachment {
    display: flex;
    align-items: center;
    padding: 10px;
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    transition: background-color 0.3s;
    cursor: pointer;
    font-weight: bold;
    gap: 6px;

    &:not(.default) {
      color: #337ab7;

      svg {
        fill: #337ab7;
      }

      &:hover {
        background-color: #f1f1f1;
        color: #007bff;

        svg {
          fill: #007bff;
        }
      }

      &:focus {
        outline: 2px solid #007bff;
      }
    }
  }

  &__skeleton {
    flex-grow: 1;
    height: 40px;
    border-radius: 4px;
  }
}
</style>
